import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Autocomplete, ListItemIcon, ListItemText, InputLabel, TextField, Box } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';

const TeamDropdown = (props) => {
  const {
    value,
    onChange,
    readonly,
    teamsMapValues = {},
    scores = {}
  } = props;

  const scoredTeamUids = React.useMemo(() => {
    const s = new Set();
    for (const k of Object.keys(scores)) {
      const t = scores[k]?.teamUid;
      if (t) s.add(t);
    }
    return s;
  }, [scores]);

  const sortedTeamIds = React.useMemo(() => {
    const ids = Object.keys(teamsMapValues);
    // Unscored first (false < true), then by numeric teamNumber (fallback name)
    return ids.sort((a, b) => {
      const aScored = scoredTeamUids.has(a);
      const bScored = scoredTeamUids.has(b);
      if (aScored !== bScored) return aScored ? 1 : -1;

      const aNum = Number(teamsMapValues[a]?.teamNumber);
      const bNum = Number(teamsMapValues[b]?.teamNumber);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;

      const aName = teamsMapValues[a]?.teamName || '';
      const bName = teamsMapValues[b]?.teamName || '';
      return aName.localeCompare(bName);
    });
  }, [teamsMapValues, scoredTeamUids]);

    const labelFor = (team) =>
    [team?.teamNumber, team?.teamName].filter(Boolean).join(' - ');

  return (
    <Grid2>
      <FormControl className="scoring-form-div" variant="standard" sx={{ minWidth: '200px' }}>
        <InputLabel id="team-select-label">Team</InputLabel>
        <Select
          labelId="team-select-label"
          name="teamUid"
          value={value ?? ''}
          onChange={(e) => onChange(e, teamsMapValues[e.target.value] || {})}
          disabled={readonly}
          size="small"
          renderValue={(selected) => {
            if (!selected) return null; // keep label-as-placeholder behavior
            const team = teamsMapValues[selected] || {};
            const isScored = scoredTeamUids.has(selected);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isScored && <CheckCircleOutline fontSize="small" color="success" />}
                <span>{labelFor(team)}</span>
              </Box>
            );
          }}
        >
          <MenuItem value="">
            <em>Team</em>
          </MenuItem>

          {sortedTeamIds.map((tUid) => {
            const team = teamsMapValues[tUid] || {};
            const isScored = scoredTeamUids.has(tUid);

            return (
              <MenuItem key={tUid} value={tUid}>
                {isScored && (
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <CheckCircleOutline fontSize="small" color="success" />
                  </ListItemIcon>
                )}
                <ListItemText primary={labelFor(team)} />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Grid2>
  );
};

const MultiSearchSelectTeamWrapper = (props) => {
    // NOTE - this is because we were at first only storing an array of UIDs to associate
    //  with the EventForm
    const {
        selectedTeams = [],
        teamsMapValues
    } = props
    const newValues = Object.keys(teamsMapValues).reduce((acc, v) => {
        if (selectedTeams.includes(v)) {
            acc = acc.concat({
                ...teamsMapValues[v],
                ...{
                    uid: v
                }
            })
        }
        return acc
    }, [])

    return (
        <MultiSearchSelectTeam
            {...props}
            value={newValues}
        />
    )
}

const MultiSearchSelectTeam = (props) => {
    const {
        value,
        onChange,
        readonly,
        teamsMapValues = {}
    } = props

    const filterMethod = (options, { inputValue }) => {
        return options.filter(item => {
            if(item.teamName == undefined) {
                return;
            }

            return item.teamName.toLowerCase().includes(inputValue)
                || item.teamNumber.toString().includes(inputValue)
        })
    }
    const teamsList = Object.keys(teamsMapValues).map(v => ({ ...teamsMapValues[v], uid: v }))

    // sort by numeric teamNumber then name
    teamsList.sort((a, b) => {
        const an = Number(a.teamNumber ?? Infinity);
        const bn = Number(b.teamNumber ?? Infinity);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
        return (a.teamName || '').localeCompare(b.teamName || '');
    });

    return (
        <>
            <Autocomplete
                fullWidth
                readOnly={readonly}
                multiple
                filterOptions={filterMethod}
                getOptionLabel={(option) => `${option.teamNumber} - ${option.teamName}`}
                options={teamsList}
                renderInput={(params) => <TextField {...params} label="Teams" />}
                isOptionEqualToValue={(option, value) => {
                    return option.uid === value.uid
                }}
                value={value}
                onChange={(event, value, reason, details) => {
                    onChange(value)
                }}
            />
        </>
    )
}

export {
    TeamDropdown,
    MultiSearchSelectTeam,
    MultiSearchSelectTeamWrapper
}
